import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
}

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = 'https://res.cloudinary.com/dtc6oth0i/image/upload/v1764387886/ChatGPT_Image_Nov_28_2025_10_44_12_PM_idoizu.png'
}: SEOProps) {
  const fullTitle = `${title} | SNOWBLOWR`;
  const baseUrl = 'https://www.snowblowr.com';
  const fullCanonical = canonical ? `${baseUrl}${canonical}` : baseUrl;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="SNOWBLOWR - On-Demand Snow Removal in Canada" />
      <meta property="og:site_name" content="SNOWBLOWR" />
      <meta property="og:locale" content="en_CA" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="SNOWBLOWR - On-Demand Snow Removal in Canada" />
      <meta name="twitter:site" content="@snowblowr" />
      <meta name="twitter:creator" content="@snowblowr" />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Mobile */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content="SNOWBLOWR" />
      
      {/* Theme */}
      <meta name="theme-color" content="#1a365d" />
      <meta name="msapplication-TileColor" content="#1a365d" />

      {/* Geographic */}
      <meta name="geo.region" content="CA" />
      <meta name="geo.placename" content="Canada" />
    </Helmet>
  );
}

// Predefined SEO configs for each page
export const SEO_CONFIG = {
  home: {
    title: 'On-Demand Snow Removal in Canada',
    description: 'SNOWBLOWR connects you to verified local operators who clear your driveway in 2-4 hours. No contracts, no surprises—just fast, reliable snow removal. Book on-demand or subscribe to Storm Pass.',
    keywords: 'snow removal, snow plowing, driveway clearing, snow removal service, on-demand snow removal, Canada, Ottawa, Montreal, Toronto',
    canonical: '/'
  },
  operators: {
    title: 'Become a Snow Removal Operator - Earn $500-2000/Week',
    description: 'Turn winter into income with SNOWBLOWR. Keep 75-85% of every job. Set your own hours. Get paid weekly or instantly. Join 500+ operators earning with their snowblower or plow truck.',
    keywords: 'snow removal jobs, snowplow operator, winter job, side hustle, gig economy, snow clearing business',
    canonical: '/operators'
  },
  faq: {
    title: 'Frequently Asked Questions - How SNOWBLOWR Works',
    description: 'Get answers about SNOWBLOWR snow removal service. Learn about pricing, how fast we clear driveways, operator requirements, Storm Pass subscriptions, and service areas across Canada.',
    keywords: 'snow removal FAQ, how does snowblowr work, snow removal pricing, operator questions',
    canonical: '/faq'
  },
  stormPass: {
    title: 'Storm Pass - Auto Snow Removal Subscription | Never Shovel Again',
    description: 'Storm Pass auto-clears your driveway every storm. No surge pricing, priority matching, and save 10-20% vs pay-per-job. Set your threshold and never think about snow again.',
    keywords: 'storm pass, snow removal subscription, automatic snow clearing, seasonal snow removal, winter subscription',
    canonical: '/storm-pass'
  },
  apply: {
    title: 'Apply to Become an Operator - Join SNOWBLOWR',
    description: 'Apply to become a SNOWBLOWR operator in under 5 minutes. Start earning with your snowblower or plow truck. Background check takes 24-48 hours. Weekly payouts or instant transfer available.',
    keywords: 'apply snow removal operator, become snow plow operator, snowblowr application, join snowblowr',
    canonical: '/apply'
  },
  cities: {
    title: 'Cities We Serve - On-Demand Snow Removal Across Canada',
    description: 'SNOWBLOWR is available in Ottawa, Montreal, and expanding to Toronto, Calgary, and Edmonton. Find local snow removal operators in your Canadian city. Join the waitlist for your area.',
    keywords: 'snow removal cities, snowblowr locations, ottawa snow removal, montreal snow removal, toronto snow plowing',
    canonical: '/cities'
  }
};

